<template>
  <div class="rt-history">
    <div class="tab-header">
      <button v-for="tab in tabs" :key="tab.key"
        :class="['tab-btn', { active: activeTab === tab.key }]"
        @click="switchTab(tab.key)">
        {{ tab.label }}
      </button>
    </div>

    <!-- 탭 1: 이동 기록 -->
    <div v-if="activeTab === 'movements'" class="tab-content">
      <div class="filter-bar">
        <div class="date-presets">
          <button :class="['preset-btn', { active: datePreset === 'today' }]" @click="setDatePreset('today')">오늘</button>
          <button :class="['preset-btn', { active: datePreset === 'week' }]" @click="setDatePreset('week')">1주일</button>
          <button :class="['preset-btn', { active: datePreset === 'month' }]" @click="setDatePreset('month')">1개월</button>
          <button :class="['preset-btn', { active: datePreset === 'custom' }]" @click="datePreset = 'custom'">직접선택</button>
        </div>
        <input type="date" v-model="filter.start_date">
        <span class="filter-sep">~</span>
        <input type="date" v-model="filter.end_date">
        <input type="text" v-model="filter.devicename" placeholder="RT명 검색" class="input-search">
        <select v-model="filter.from_location">
          <option value="">출발지 전체</option>
          <option v-for="loc in locationOptions" :key="'from-'+loc" :value="loc">{{ loc }}</option>
        </select>
        <select v-model="filter.to_location">
          <option value="">도착지 전체</option>
          <option v-for="loc in locationOptions" :key="'to-'+loc" :value="loc">{{ loc }}</option>
        </select>
        <button class="btn-search" @click="fetchMovements(1)">검색</button>
        <button class="btn-reset" @click="resetFilter">초기화</button>
      </div>

      <div v-if="movements.length > 0">
        <table class="data-table">
          <thead>
            <tr>
              <th>RT명</th>
              <th>출발지</th>
              <th>도착지</th>
              <th>출발시간</th>
              <th>도착시간</th>
              <th>소요시간</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in movements" :key="m.id" class="clickable-row" @click="goToTimeline(m.devicename)">
              <td class="td-name">{{ m.devicename || m.tag_mac }}</td>
              <td>{{ m.from_location || '-' }}</td>
              <td>{{ m.to_location || '-' }}</td>
              <td class="td-time">{{ formatDate(m.depart_time) }}</td>
              <td class="td-time">{{ formatDate(m.arrive_time) }}</td>
              <td class="td-time">{{ calcDuration(m.depart_time, m.arrive_time) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="movementsPagination.totalPages > 1" class="pagination">
          <button :disabled="movementsPagination.page <= 1" @click="fetchMovements(movementsPagination.page - 1)">&lt;</button>
          <span>{{ movementsPagination.page }} / {{ movementsPagination.totalPages }} ({{ movementsPagination.total }}건)</span>
          <button :disabled="movementsPagination.page >= movementsPagination.totalPages" @click="fetchMovements(movementsPagination.page + 1)">&gt;</button>
        </div>
      </div>
      <div v-else class="empty-state">이동 기록이 없습니다</div>
    </div>

    <!-- 탭 2: RT 추적 -->
    <div v-if="activeTab === 'tracking'" class="tab-content">
      <div class="tracking-header">
        <div class="rt-selector">
          <input type="text" v-model="rtSearchText" placeholder="RT 검색..." class="input-search" @input="filterRtList">
          <select v-model="selectedRt" @change="fetchTimeline" class="rt-dropdown">
            <option value="">RT 선택</option>
            <option v-for="rt in filteredRtList" :key="rt.tag_mac" :value="rt.devicename">{{ rt.devicename }}</option>
          </select>
        </div>
      </div>

      <div v-if="selectedRt && timelineData" class="tracking-content">
        <div class="info-cards">
          <div class="info-card">
            <div class="info-label">현재 위치</div>
            <div class="info-value">{{ timelineData.currentLocation || '-' }}</div>
          </div>
          <div class="info-card">
            <div class="info-label">마지막 감지</div>
            <div class="info-value">{{ formatDate(timelineData.lastSeen) }}</div>
          </div>
          <div class="info-card">
            <div class="info-label">총 이동횟수</div>
            <div class="info-value">{{ timelineData.totalMoves }}회</div>
          </div>
        </div>

        <div v-if="timelineData.timeline.length > 0" class="timeline-container">
          <div class="timeline">
            <div v-for="(item, idx) in timelineData.timeline" :key="item.id" class="timeline-item">
              <div class="timeline-dot" :class="{ first: idx === 0 }"></div>
              <div class="timeline-content">
                <div class="timeline-route">
                  <span class="loc from">{{ item.from_location || '?' }}</span>
                  <span class="arrow">&rarr;</span>
                  <span class="loc to">{{ item.to_location || '?' }}</span>
                </div>
                <div class="timeline-meta">
                  <span>{{ formatDate(item.depart_time) }} ~ {{ formatDate(item.arrive_time) }}</span>
                  <span class="duration">{{ calcDuration(item.depart_time, item.arrive_time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">이동 이력이 없습니다</div>
      </div>
      <div v-else class="empty-state">RT를 선택하세요</div>
    </div>

    <!-- 탭 3: 통계 -->
    <div v-if="activeTab === 'stats'" class="tab-content">
      <div class="filter-bar">
        <div class="date-presets">
          <button :class="['preset-btn', { active: statsDatePreset === 'week' }]" @click="setStatsDatePreset('week')">1주일</button>
          <button :class="['preset-btn', { active: statsDatePreset === 'month' }]" @click="setStatsDatePreset('month')">1개월</button>
          <button :class="['preset-btn', { active: statsDatePreset === '3month' }]" @click="setStatsDatePreset('3month')">3개월</button>
          <button :class="['preset-btn', { active: statsDatePreset === 'custom' }]" @click="statsDatePreset = 'custom'">직접선택</button>
        </div>
        <input type="date" v-model="statsFilter.start_date">
        <span class="filter-sep">~</span>
        <input type="date" v-model="statsFilter.end_date">
        <button class="btn-search" @click="fetchAllStats">검색</button>
      </div>

      <div class="stats-grid">
        <!-- 일별 추이 -->
        <div class="chart-card">
          <h3 class="chart-title">일별 이동 추이</h3>
          <div class="chart-wrapper"><canvas ref="dailyChart"></canvas></div>
        </div>
        <!-- 노선별 빈도 -->
        <div class="chart-card">
          <h3 class="chart-title">노선별 빈도 (상위)</h3>
          <div class="chart-wrapper"><canvas ref="routeChart"></canvas></div>
        </div>
        <!-- 장소별 활동 -->
        <div class="chart-card">
          <h3 class="chart-title">장소별 활동</h3>
          <div class="chart-wrapper"><canvas ref="locationChart"></canvas></div>
        </div>
        <!-- 이동 흐름 -->
        <div class="chart-card">
          <h3 class="chart-title">지역 간 이동 흐름</h3>
          <div class="flow-table-wrapper" v-if="flowData.length > 0">
            <table class="data-table flow-table">
              <thead><tr><th>출발 지역</th><th>도착 지역</th><th>이동 건수</th><th>비율</th></tr></thead>
              <tbody>
                <tr v-for="(f, idx) in flowData" :key="idx">
                  <td>{{ f.from_group }}</td>
                  <td>{{ f.to_group }}</td>
                  <td>{{ f.flow_count }}</td>
                  <td>
                    <div class="flow-bar-container">
                      <div class="flow-bar" :style="{ width: flowPercent(f.flow_count) + '%' }"></div>
                      <span class="flow-pct">{{ flowPercent(f.flow_count) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">데이터가 없습니다</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

export default {
  data() {
    return {
      activeTab: 'movements',
      tabs: [
        { key: 'movements', label: '이동 기록' },
        { key: 'tracking', label: 'RT 추적' },
        { key: 'stats', label: '통계' }
      ],
      // 이동 기록
      movements: [],
      movementsPagination: { page: 1, totalPages: 1, total: 0 },
      filter: { start_date: '', end_date: '', from_location: '', to_location: '', devicename: '' },
      datePreset: 'week',
      locationOptions: [],
      // RT 추적
      rtList: [],
      filteredRtList: [],
      rtSearchText: '',
      selectedRt: '',
      timelineData: null,
      // 통계
      statsFilter: { start_date: '', end_date: '' },
      statsDatePreset: 'month',
      flowData: [],
      flowTotal: 0,
      // 차트 인스턴스
      dailyChartInstance: null,
      routeChartInstance: null,
      locationChartInstance: null
    };
  },

  methods: {
    switchTab(key) {
      this.activeTab = key;
      if (key === 'movements' && this.movements.length === 0) this.fetchMovements(1);
      if (key === 'tracking' && this.rtList.length === 0) this.fetchRtList();
      if (key === 'stats') {
        this.$nextTick(() => this.fetchAllStats());
      }
    },

    // === 날짜 프리셋 ===
    setDatePreset(preset) {
      this.datePreset = preset;
      const today = new Date();
      this.filter.end_date = this.toDateStr(today);
      if (preset === 'today') {
        this.filter.start_date = this.toDateStr(today);
      } else if (preset === 'week') {
        const d = new Date(today);
        d.setDate(d.getDate() - 7);
        this.filter.start_date = this.toDateStr(d);
      } else if (preset === 'month') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        this.filter.start_date = this.toDateStr(d);
      }
      this.fetchMovements(1);
    },

    setStatsDatePreset(preset) {
      this.statsDatePreset = preset;
      const today = new Date();
      this.statsFilter.end_date = this.toDateStr(today);
      if (preset === 'week') {
        const d = new Date(today);
        d.setDate(d.getDate() - 7);
        this.statsFilter.start_date = this.toDateStr(d);
      } else if (preset === 'month') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 1);
        this.statsFilter.start_date = this.toDateStr(d);
      } else if (preset === '3month') {
        const d = new Date(today);
        d.setMonth(d.getMonth() - 3);
        this.statsFilter.start_date = this.toDateStr(d);
      }
      this.fetchAllStats();
    },

    toDateStr(d) {
      return d.toISOString().slice(0, 10);
    },

    // === 이동 기록 ===
    async fetchMovements(page) {
      try {
        const params = { page, limit: 50 };
        if (this.filter.start_date) params.start_date = this.filter.start_date;
        if (this.filter.end_date) params.end_date = this.filter.end_date;
        if (this.filter.from_location) params.from_location = this.filter.from_location;
        if (this.filter.to_location) params.to_location = this.filter.to_location;
        if (this.filter.devicename) params.devicename = this.filter.devicename;

        const response = await axios.get('/api/rt-history/movements', { params });
        this.movements = response.data.data || [];
        this.movementsPagination = response.data.pagination || { page: 1, totalPages: 1, total: 0 };

        // 장소 옵션 추출
        if (this.locationOptions.length === 0) this.extractLocations();
      } catch (error) {
        console.error('이동 기록 조회 실패:', error);
      }
    },

    async extractLocations() {
      try {
        const response = await axios.get('/api/rt-history/stats/locations');
        if (response.data.success) {
          this.locationOptions = response.data.data.map(d => d.location).filter(Boolean);
        }
      } catch (e) {
        // 무시
      }
    },

    resetFilter() {
      this.filter = { start_date: '', end_date: '', from_location: '', to_location: '', devicename: '' };
      this.datePreset = '';
      this.fetchMovements(1);
    },

    goToTimeline(devicename) {
      if (!devicename) return;
      this.selectedRt = devicename;
      this.activeTab = 'tracking';
      if (this.rtList.length === 0) {
        this.fetchRtList().then(() => this.fetchTimeline());
      } else {
        this.fetchTimeline();
      }
    },

    // === RT 추적 ===
    async fetchRtList() {
      try {
        const response = await axios.get('/api/rt-history/rt-list');
        this.rtList = response.data.data || [];
        this.filteredRtList = this.rtList;
      } catch (error) {
        console.error('RT 목록 조회 실패:', error);
      }
    },

    filterRtList() {
      const q = this.rtSearchText.toLowerCase();
      this.filteredRtList = q
        ? this.rtList.filter(rt => rt.devicename && rt.devicename.toLowerCase().includes(q))
        : this.rtList;
    },

    async fetchTimeline() {
      if (!this.selectedRt) {
        this.timelineData = null;
        return;
      }
      try {
        const response = await axios.get(`/api/rt-history/timeline/${encodeURIComponent(this.selectedRt)}`);
        this.timelineData = response.data.data || { timeline: [], currentLocation: null, totalMoves: 0 };
      } catch (error) {
        console.error('타임라인 조회 실패:', error);
      }
    },

    // === 통계 ===
    async fetchAllStats() {
      await Promise.all([
        this.fetchDailyStats(),
        this.fetchRouteStats(),
        this.fetchLocationStats(),
        this.fetchFlowStats()
      ]);
    },

    async fetchDailyStats() {
      try {
        const params = {};
        if (this.statsFilter.start_date) params.start_date = this.statsFilter.start_date;
        if (this.statsFilter.end_date) params.end_date = this.statsFilter.end_date;
        const response = await axios.get('/api/rt-history/stats/daily', { params });
        const data = response.data.data || [];
        this.renderDailyChart(data);
      } catch (error) {
        console.error('일별 통계 조회 실패:', error);
      }
    },

    async fetchRouteStats() {
      try {
        const params = {};
        if (this.statsFilter.start_date) params.start_date = this.statsFilter.start_date;
        if (this.statsFilter.end_date) params.end_date = this.statsFilter.end_date;
        const response = await axios.get('/api/rt-history/stats/routes', { params });
        const data = response.data.data || [];
        this.renderRouteChart(data);
      } catch (error) {
        console.error('노선별 통계 조회 실패:', error);
      }
    },

    async fetchLocationStats() {
      try {
        const params = {};
        if (this.statsFilter.start_date) params.start_date = this.statsFilter.start_date;
        if (this.statsFilter.end_date) params.end_date = this.statsFilter.end_date;
        const response = await axios.get('/api/rt-history/stats/locations', { params });
        const data = response.data.data || [];
        this.renderLocationChart(data);
      } catch (error) {
        console.error('장소별 통계 조회 실패:', error);
      }
    },

    async fetchFlowStats() {
      try {
        const params = {};
        if (this.statsFilter.start_date) params.start_date = this.statsFilter.start_date;
        if (this.statsFilter.end_date) params.end_date = this.statsFilter.end_date;
        const response = await axios.get('/api/rt-history/stats/flow', { params });
        this.flowData = response.data.data || [];
        this.flowTotal = this.flowData.reduce((sum, f) => sum + f.flow_count, 0);
      } catch (error) {
        console.error('이동 흐름 통계 조회 실패:', error);
      }
    },

    // === 차트 렌더링 ===
    renderDailyChart(data) {
      if (this.dailyChartInstance) this.dailyChartInstance.destroy();
      const canvas = this.$refs.dailyChart;
      if (!canvas) return;

      this.dailyChartInstance = new Chart(canvas, {
        type: 'line',
        data: {
          labels: data.map(d => d.date ? d.date.slice(5) : ''),
          datasets: [
            {
              label: '이동 건수',
              data: data.map(d => d.move_count),
              borderColor: '#1976d2',
              backgroundColor: 'rgba(25,118,210,0.1)',
              fill: true,
              tension: 0.3
            },
            {
              label: '고유 RT 수',
              data: data.map(d => d.unique_rt_count),
              borderColor: '#ff9800',
              backgroundColor: 'rgba(255,152,0,0.1)',
              fill: true,
              tension: 0.3
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { datalabels: { display: false } }
        }
      });
    },

    renderRouteChart(data) {
      if (this.routeChartInstance) this.routeChartInstance.destroy();
      const canvas = this.$refs.routeChart;
      if (!canvas) return;

      const top10 = data.slice(0, 10);
      this.routeChartInstance = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: top10.map(d => `${d.from_location || '?'} → ${d.to_location || '?'}`),
          datasets: [{
            label: '이동 횟수',
            data: top10.map(d => d.trip_count),
            backgroundColor: '#42a5f5'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            datalabels: {
              anchor: 'end',
              align: 'end',
              font: { size: 11 },
              color: '#555'
            }
          }
        },
        plugins: [ChartDataLabels]
      });
    },

    renderLocationChart(data) {
      if (this.locationChartInstance) this.locationChartInstance.destroy();
      const canvas = this.$refs.locationChart;
      if (!canvas) return;

      const top10 = data.slice(0, 10);
      this.locationChartInstance = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: top10.map(d => d.location),
          datasets: [
            {
              label: '출발',
              data: top10.map(d => d.depart_count),
              backgroundColor: '#66bb6a'
            },
            {
              label: '도착',
              data: top10.map(d => d.arrive_count),
              backgroundColor: '#ef5350'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { datalabels: { display: false } }
        }
      });
    },

    // === 유틸 ===
    flowPercent(count) {
      if (!this.flowTotal) return 0;
      return Math.round((count / this.flowTotal) * 100);
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString('ko-KR', {
        year: '2-digit', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    },

    calcDuration(depart, arrive) {
      if (!depart || !arrive) return '-';
      const diff = Math.round((new Date(arrive) - new Date(depart)) / 60000);
      if (diff < 0) return '-';
      if (diff < 60) return `${diff}분`;
      const h = Math.floor(diff / 60);
      const m = diff % 60;
      return `${h}시간 ${m}분`;
    }
  },

  created() {
    this.setDatePreset('week');
    this.setStatsDatePreset('month');
  },

  beforeUnmount() {
    if (this.dailyChartInstance) this.dailyChartInstance.destroy();
    if (this.routeChartInstance) this.routeChartInstance.destroy();
    if (this.locationChartInstance) this.locationChartInstance.destroy();
  }
};
</script>

<style scoped>
.rt-history { width: 100%; max-width: 1200px; margin: auto; padding: 16px; }

/* 탭 */
.tab-header { display: flex; border-bottom: 2px solid #e0e0e0; margin-bottom: 16px; }
.tab-btn { flex: 1; padding: 10px 16px; border: none; background: none; font-size: 15px; font-weight: 500; color: #666; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; }
.tab-btn.active { color: #1976d2; border-bottom-color: #1976d2; font-weight: 600; }
.tab-btn:hover:not(.active) { color: #333; background: #f5f5f5; }

.tab-content { min-height: 150px; }
.empty-state { text-align: center; color: #999; padding: 30px 20px; font-size: 14px; }

/* 필터 */
.filter-bar { display: flex; gap: 8px; margin-bottom: 12px; align-items: center; flex-wrap: wrap; }
.date-presets { display: flex; gap: 4px; margin-right: 8px; }
.preset-btn { padding: 5px 10px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; font-size: 12px; color: #555; }
.preset-btn.active { background: #1976d2; color: white; border-color: #1976d2; }
.filter-bar select, .filter-bar input[type="date"] { padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
.input-search { padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; width: 120px; }
.filter-sep { color: #999; }
.btn-search { background: #1976d2; color: white; border: none; padding: 6px 14px; border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600; }
.btn-search:hover { background: #1565c0; }
.btn-reset { background: #f5f5f5; color: #555; border: 1px solid #ddd; padding: 6px 10px; border-radius: 4px; cursor: pointer; font-size: 13px; }

/* 테이블 */
.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th { background: #f5f5f5; padding: 8px 10px; text-align: left; font-weight: 600; color: #555; border-bottom: 2px solid #ddd; white-space: nowrap; }
.data-table td { padding: 7px 10px; border-bottom: 1px solid #eee; vertical-align: middle; }
.data-table tbody tr:hover { background: #fafafa; }
.clickable-row { cursor: pointer; }
.clickable-row:hover { background: #e3f2fd !important; }
.td-name { font-weight: 600; color: #333; }
.td-time { color: #888; font-size: 12px; white-space: nowrap; }

.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; font-size: 13px; }
.pagination button { background: #f5f5f5; border: 1px solid #ddd; padding: 4px 12px; border-radius: 4px; cursor: pointer; }
.pagination button:disabled { opacity: 0.4; cursor: not-allowed; }

/* RT 추적 */
.tracking-header { margin-bottom: 16px; }
.rt-selector { display: flex; gap: 8px; align-items: center; }
.rt-dropdown { padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; min-width: 200px; }

.info-cards { display: flex; gap: 12px; margin-bottom: 16px; }
.info-card { flex: 1; background: #f9f9f9; border-radius: 8px; padding: 14px; text-align: center; }
.info-label { font-size: 12px; color: #888; margin-bottom: 4px; }
.info-value { font-size: 18px; font-weight: 600; color: #333; }

/* 타임라인 */
.timeline-container { max-height: 500px; overflow-y: auto; }
.timeline { position: relative; padding-left: 24px; border-left: 2px solid #e0e0e0; margin-left: 12px; }
.timeline-item { position: relative; padding: 0 0 20px 16px; }
.timeline-dot { position: absolute; left: -31px; top: 4px; width: 12px; height: 12px; background: #bbb; border-radius: 50%; border: 2px solid #fff; }
.timeline-dot.first { background: #1976d2; width: 14px; height: 14px; left: -32px; }
.timeline-content { background: #fafafa; border-radius: 6px; padding: 10px 14px; }
.timeline-route { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
.loc.from { color: #2e7d32; }
.loc.to { color: #c62828; }
.arrow { margin: 0 6px; color: #999; }
.timeline-meta { font-size: 12px; color: #888; display: flex; justify-content: space-between; }
.duration { font-weight: 600; color: #1976d2; }

/* 통계 */
.stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.chart-card { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 16px; }
.chart-title { font-size: 14px; font-weight: 600; color: #333; margin-bottom: 12px; }
.chart-wrapper { height: 260px; position: relative; }

/* 이동 흐름 테이블 */
.flow-table-wrapper { max-height: 260px; overflow-y: auto; }
.flow-table { font-size: 12px; }
.flow-bar-container { display: flex; align-items: center; gap: 6px; }
.flow-bar { height: 16px; background: #42a5f5; border-radius: 3px; min-width: 2px; }
.flow-pct { font-size: 11px; color: #888; white-space: nowrap; }

@media screen and (max-width: 768px) {
  .stats-grid { grid-template-columns: 1fr; }
  .info-cards { flex-direction: column; }
  .filter-bar { flex-direction: column; align-items: stretch; }
  .date-presets { justify-content: center; }
}
</style>
